import { GoogleOAuthProvider } from '@react-oauth/google'
import { type Dayjs } from 'dayjs'
import {
  useSelectedDate,
  useSelectedDateDispatch,
} from '../../../contexts/SelectedDate'
import { useSelectedRecordNameDispatch } from '../../../contexts/SelectedRecordName'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { Timeline } from '../../calendar/timeline/Timeline'
import { Calendar } from '../../calendar/Calendar'
import CalendarMobile from '../../calendar/CalendarMobile'
import { useIsOnline } from '../../../providers/NetworkProvider'
import classNames from 'classnames'
import { MsalProvider } from '@azure/msal-react'
import { isMsalEnabled, msalInstance } from '../../../lib/msal/authConfig'

export function CalendarView({
  showCalendar,
  toggleCalendar,
  toggleSidebar,
  isEmbedded = false,
}: {
  showCalendar: boolean
  toggleCalendar: () => void
  toggleSidebar: () => void
  isEmbedded?: boolean
}) {
  const isMobile = useIsMobile()
  const selectedDate = useSelectedDate()
  const selectedDateDispatch = useSelectedDateDispatch()
  const setSelectedRecordName = useSelectedRecordNameDispatch()
  const isOnline = useIsOnline()

  const timelineDays = 1

  function setToday() {
    selectedDateDispatch({ type: 'today', forceDay: true })
    setSelectedRecordName('daily')
  }

  function setWeek({ week, year }: { week: number; year: number }) {
    selectedDateDispatch({ type: 'setWeek', week, year })
    setSelectedRecordName('weekly')
  }

  function setDay(date: Dayjs) {
    selectedDateDispatch({ type: 'setDay', date })
    setSelectedRecordName('daily')
  }

  const CalendarContent = () => (
    <>
      {isMobile ? (
        <div
          className={classNames('relative', {
            'opacity-10': !isOnline,
          })}
        >
          <CalendarMobile
            selectedDate={selectedDate}
            onToday={setToday}
            onChangeWeek={setWeek}
            onChangeDay={setDay}
            showCalendar={showCalendar}
            onToggleSidebar={toggleSidebar}
          />
          {!isOnline && <div className='absolute left-0 top-0 h-full w-full' />}
        </div>
      ) : (
        <>
          {isEmbedded ? (
            // When embedded in RightSidebarWrapper, don't render the chevron
            <div
              className={classNames('h-dvh w-full', {
                'opacity-10': !isOnline,
              })}
            >
              <div className='relative flex h-full w-full flex-col items-center justify-center space-y-2 overflow-hidden'>
                <div className='w-full max-w-full overflow-hidden'>
                  <Calendar
                    selectedDate={selectedDate}
                    onToday={setToday}
                    onChangeWeek={setWeek}
                    onChangeDay={setDay}
                    timelineDays={timelineDays}
                    responsive={true}
                  />
                </div>
                <div className='w-full max-w-full overflow-hidden'>
                  <Timeline timelineDays={timelineDays} responsive={true} />
                </div>
                {!isOnline && (
                  <div className='absolute left-0 top-0 h-full w-full' />
                )}
              </div>
            </div>
          ) : (
            // Original standalone calendar with chevron
            <div className='sticky top-0 flex h-dvh max-h-dvh select-none items-center justify-center'>
              <button
                type='button'
                onClick={toggleCalendar}
                className='-ml-6 bg-transparent py-4 opacity-25 transition hover:bg-gray-200 hover:opacity-100 dark:hover:bg-gray-700'
              >
                <i
                  className={`fa-solid fa-chevron-${showCalendar ? 'right' : 'left'}`}
                />
              </button>
              {showCalendar && (
                <div
                  className={classNames('h-dvh border-l-2', {
                    'opacity-10': !isOnline,
                  })}
                >
                  <div className='relative hidden flex-col items-center space-y-2 md:flex'>
                    <Calendar
                      selectedDate={selectedDate}
                      onToday={setToday}
                      onChangeWeek={setWeek}
                      onChangeDay={setDay}
                      timelineDays={timelineDays}
                    />
                    <Timeline timelineDays={timelineDays} />
                    {!isOnline && (
                      <div className='absolute left-0 top-0 h-full w-full' />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </>
  )

  return (
    <GoogleOAuthProvider clientId={process.env.API_GOOGLE_CLIENTID ?? ''}>
      {isMsalEnabled && msalInstance ? (
        <MsalProvider instance={msalInstance}>
          <CalendarContent />
        </MsalProvider>
      ) : (
        <CalendarContent />
      )}
    </GoogleOAuthProvider>
  )
}
